/**
 * IFD Subscription Widget Controls
 * Works alongside the Appstle Subscriptions app block on the product page.
 * - Hides the "One-Time Purchase" option and auto-selects the subscription
 *   plan when a "Monthly" variant is selected.
 * - Syncs the theme's price display with the selected selling plan price.
 * - Adds an add-on product to the cart whenever a subscription is added.
 */
(function () {
  'use strict';

  const MONTHLY_PATTERN = /monthly/i;
  const ONE_TIME_PATTERN = /one[\s-]?time/i;
  const APPLY_RETRY_DELAYS = [50, 250, 600, 1200, 2500];

  function formatMoney(cents, format) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (number == null || Number.isNaN(number)) number = 0;
      const parts = (number / 100).toFixed(precision).split('.');
      const units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? units + decimal + parts[1] : units;
    }

    let value;
    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'");
        break;
      default:
        value = withDelimiters(cents, 2);
        break;
    }

    return format.replace(placeholderRegex, value);
  }

  class IfdSubscriptionController {
    constructor(configEl) {
      this.config = JSON.parse(configEl.textContent);
      this.section = configEl.closest('.shopify-section') || document;
      this.addonVariantId = this.config.addonVariantId;
      this.forcedSubscription = false;
      this.applyTimeout = null;
      this.autoClicks = 0;

      this.applyState = this.applyState.bind(this);

      this.addListeners();
      this.initAddonFallback();
      APPLY_RETRY_DELAYS.forEach((ms) => setTimeout(this.applyState, ms));
    }

    addListeners() {
      document.addEventListener('on:variant:change', () => {
        this.autoClicks = 0;
        APPLY_RETRY_DELAYS.forEach((ms) => setTimeout(this.applyState, ms));
      });

      // Re-sync price when a purchase option/selling plan changes
      document.addEventListener('change', (evt) => {
        const widget = this.getWidget();
        if (evt.target.name === 'selling_plan' || (widget && widget.contains(evt.target))) {
          setTimeout(() => this.syncPrice(), 50);
        }
      });

      // The Appstle widget renders/rebuilds asynchronously
      this.observer = new MutationObserver(() => this.scheduleApply());
      this.observer.observe(this.section, { childList: true, subtree: true });

      // Add the add-on product before the main subscription item is submitted
      document.addEventListener('submit', this.handleSubmit.bind(this), true);
    }

    scheduleApply() {
      clearTimeout(this.applyTimeout);
      this.applyTimeout = setTimeout(this.applyState, 150);
    }

    getForm() {
      return this.section.querySelector('form.js-product-form-main');
    }

    getWidget() {
      return this.section.querySelector(
        '.appstle_sub_widget, [class*="appstle_subscription_wrapper"], [id^="appstle_subscription"]'
      );
    }

    /**
     * Finds the selected selling plan value, wherever Appstle keeps it:
     * a form-associated element, a hidden input, or the widget's own
     * checked radio/select.
     */
    getSellingPlanValue() {
      const form = this.getForm();
      if (form && form.elements) {
        const el = form.elements.namedItem('selling_plan');
        if (el && typeof el.value === 'string' && el.value) return el.value;
      }
      const candidates = [
        'input[name="selling_plan"]:checked',
        'input[name="selling_plan"][type="hidden"]',
        'select[name="selling_plan"]',
        'input[name="selling_plan"]'
      ];
      for (let i = 0; i < candidates.length; i += 1) {
        const el = this.section.querySelector(candidates[i]) || document.querySelector(candidates[i]);
        if (el && el.value) return el.value;
      }
      return '';
    }

    /**
     * True when a subscription purchase option is selected, even if no
     * selling_plan input can be found (falls back to the widget radio).
     */
    isSubscriptionSelected() {
      if (this.getSellingPlanValue()) return true;
      const widget = this.getWidget();
      if (widget) {
        const subscription = this.getOptionRows(widget).find((r) => !r.isOneTime);
        if (subscription && subscription.radio.checked) return true;
      }
      return false;
    }

    getCurrentVariant() {
      const form = this.getForm();
      const id = Number(form && form.querySelector('input[name="id"]') && form.querySelector('input[name="id"]').value);
      return this.config.variants.find((v) => v.id === id) || null;
    }

    static isMonthly(variant) {
      return MONTHLY_PATTERN.test(variant.title)
        || (variant.options || []).some((opt) => MONTHLY_PATTERN.test(opt));
    }

    /**
     * Maps each radio in the Appstle widget to its full option row and
     * classifies it as one-time or subscription.
     */
    getOptionRows(widget) {
      const radios = Array.from(widget.querySelectorAll('input[type="radio"]'));
      return radios.map((radio) => {
        let row = radio;
        while (
          row.parentElement
          && row.parentElement !== widget
          && row.parentElement !== document.body
          && row.parentElement.querySelectorAll('input[type="radio"]').length < 2
        ) {
          row = row.parentElement;
        }
        const text = row.textContent || '';
        const isOneTime = (!/subscri/i.test(text) && (radio.value === '' || radio.value === '0'))
          || (ONE_TIME_PATTERN.test(text) && !/subscri/i.test(text))
          || /one[\s_-]?time/i.test(radio.className + ' ' + (row.className || ''));
        return { radio, row, isOneTime };
      });
    }

    applyState() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      const monthly = IfdSubscriptionController.isMonthly(variant) && variant.allocations.length > 0;
      const productInfo = this.section.querySelector('.product-info');
      if (productInfo && productInfo.classList.contains('ifd-hide-onetime') !== monthly) {
        productInfo.classList.toggle('ifd-hide-onetime', monthly);
      }

      const widget = this.getWidget();
      if (widget) {
        const rows = this.getOptionRows(widget);
        const oneTime = rows.find((r) => r.isOneTime);
        const subscription = rows.find((r) => !r.isOneTime);

        if (monthly) {
          if (oneTime && oneTime.row.style.display !== 'none') {
            oneTime.row.style.setProperty('display', 'none', 'important');
          }
          if (subscription && !subscription.radio.checked && this.autoClicks < 5) {
            this.autoClicks += 1;
            subscription.radio.click();
          }
          this.forcedSubscription = true;
        } else {
          if (oneTime && oneTime.row.style.display === 'none') {
            oneTime.row.style.removeProperty('display');
          }
          // Only reset to one-time when leaving a forced subscription state,
          // so a deliberate subscription choice is never overridden.
          if (this.forcedSubscription && oneTime && !oneTime.radio.checked && this.autoClicks < 5) {
            this.autoClicks += 1;
            oneTime.radio.click();
          }
          this.forcedSubscription = false;
        }
      }

      this.syncPrice();
    }

    syncPrice() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      const planValue = this.getSellingPlanValue();
      const planId = planValue ? Number(planValue) : null;
      let allocation = planId
        ? variant.allocations.find((a) => a.sellingPlanId === planId)
        : null;
      // Fall back to the first plan when the widget has a subscription
      // selected but no readable selling_plan value.
      if (!allocation && this.isSubscriptionSelected() && variant.allocations.length) {
        allocation = variant.allocations[0];
      }

      const priceEl = this.section.querySelector('.product-info__price .price');
      if (!priceEl) return;

      const displayPrice = allocation ? allocation.price : variant.price;
      let comparePrice = allocation
        ? Math.max(allocation.compareAtPrice || 0, variant.price)
        : (variant.compareAtPrice || 0);
      if (comparePrice <= displayPrice) comparePrice = 0;

      // Skip if already rendered (avoids MutationObserver feedback loops)
      const stamp = `${displayPrice}:${comparePrice}`;
      if (priceEl.dataset.ifdPriceStamp === stamp) return;
      priceEl.dataset.ifdPriceStamp = stamp;

      const currentValueEl = priceEl.querySelector('.price__current .js-value');
      const wasValueEl = priceEl.querySelector('.price__was .js-value');

      if (currentValueEl) {
        currentValueEl.innerHTML = formatMoney(displayPrice, this.config.moneyFormat);
      }
      if (wasValueEl) {
        wasValueEl.innerHTML = comparePrice ? formatMoney(comparePrice, this.config.moneyFormat) : '';
      }
      priceEl.classList.toggle('price--on-sale', comparePrice > 0);
    }

    /**
     * If no add-on product is set in the theme editor, fall back to the
     * first available product in the catalog (excluding this one).
     */
    initAddonFallback() {
      if (this.addonVariantId) return;
      fetch('/products.json?limit=10')
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (!data || !data.products) return;
          const candidate = data.products.find((p) => p.id !== this.config.productId
            && (p.variants || []).some((v) => v.available));
          if (candidate) {
            this.addonVariantId = candidate.variants.find((v) => v.available).id;
          }
        })
        .catch(() => {});
    }

    /**
     * Capture-phase submit handler: when a subscription plan is selected,
     * add the add-on product to the cart first, then resubmit the form so
     * the theme's normal add-to-cart flow (and cart drawer refresh) runs.
     */
    handleSubmit(evt) {
      const form = evt.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form.js-product-form-main')) return;
      if (!this.section.contains(form)) return;

      if (form.dataset.ifdAddonHandled === '1') {
        delete form.dataset.ifdAddonHandled;
        return;
      }

      if (!this.isSubscriptionSelected() || !this.addonVariantId) return;

      evt.preventDefault();
      evt.stopPropagation();

      const addonId = this.addonVariantId;
      const resubmit = () => {
        form.dataset.ifdAddonHandled = '1';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      };

      fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          if (cart.items.some((item) => item.variant_id === Number(addonId))) return null;
          console.debug('[IFD subscription] adding add-on variant', addonId);
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [{
                id: addonId,
                quantity: 1,
                properties: { _ifd_addon: 'subscription-addon' }
              }]
            })
          });
        })
        .catch(() => {})
        .finally(resubmit);
    }
  }

  function init() {
    document.querySelectorAll('script[data-ifd-subscription-config]').forEach((el) => {
      if (el.dataset.ifdInitialized) return;
      el.dataset.ifdInitialized = 'true';
      new IfdSubscriptionController(el); // eslint-disable-line no-new
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
